import * as React from 'react';
import PropTypes from 'prop-types';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import { Body, Meta } from '../../styled components/Typography';
import { dateFormatOptions } from '../../libs/dateFormatting';
import Divider from '../../styled components/Divider';
import Button from '../../components/Button/Button';
import TimesheetEntryItem from '../TimesheetEntryItem/TimesheetEntryItem';
import Sticky from '../../styled components/Sticky';
import { capture, PH_TIMESHEET_EDITED } from '../../libs/posthogAnalytics';
import FormField from '../../components/FormField/FormField';
import YPTimesheetObject from '../../yesplan_classes/YPTimesheetObject';
import TimesheetStatus from '../TimesheetStatus/TimesheetStatus';
import {
  TimesheetBottomActionBar,
  TimesheetContentWrapper
} from './TimesheetDetail.style';
import debounce from '../../libs/debounce';
import TimesheetLog from '../TimesheetLog/TimesheetLog';
import { timeFormatOptions } from '../../libs/timeFormatting';
import TimesheetUnavailabilities from '../TimesheetUnavailabilities/TimesheetUnavailabilities';

class TimesheetDetail extends React.Component {
  constructor(props) {
    super(props);

    const { timesheetObject } = props;

    this.state = {
      requestChangesMode: false,
      issueText: timesheetObject.getIssueText(),
      formData: {}
    };
    const entries = timesheetObject.getEntries();
    const { formData } = this.state;
    entries.forEach((entry) => {
      formData[entry.getIdWithOnlyStartOrEnd()] = entry;
    });

    ['handleTimeEdit', 'handleIssueTextChange', 'processTimeChange'].forEach(
      (eventHandler) => {
        this[eventHandler] = this[eventHandler].bind(this);
      }
    );

    this.autosaveTimesheet = debounce(this.autosaveTimesheet.bind(this), false);
  }

  handleTimeEdit(timeEntryId, prop) {
    const { formData } = this.state;
    const storedEntry = formData[timeEntryId];
    const propertyName = Object.keys(prop)[0];
    const updatedFormData = { ...formData };
    const entriesToPerformEditOn = [storedEntry];
    if (storedEntry.isShift()) {
      entriesToPerformEditOn.push(formData[storedEntry.getIdForDuplicateShiftEntry()]);
    }
    entriesToPerformEditOn.forEach((entry) => {
      if (propertyName === 'filledInEndTime') {
        entry.setTimesheetEndEdit(prop.filledInEndTime);
      } else if (propertyName === 'filledInStartTime') {
        entry.setTimesheetStartEdit(prop.filledInStartTime);
      }
      updatedFormData[entry.getIdWithOnlyStartOrEnd()] = entry;
    });
    this.setState({
      formData: updatedFormData
    });
    capture(PH_TIMESHEET_EDITED);
  }

  handleIssueTextChange(e) {
    const { value } = e.target;
    this.setState({
      issueText: value
    });
  }

  processTimeChange(timeEntryId) {
    const { formData } = this.state;
    const { timesheetObject } = this.props;
    const storedEntry = formData[timeEntryId];
    timesheetObject.processTimeChange();
    this.setState({
      formData: {
        ...formData,
        [timeEntryId]: storedEntry
      }
    });
  }

  autosaveTimesheet() {
    const { autosaveTimesheet, timesheetObject } = this.props;
    autosaveTimesheet(timesheetObject);
  }

  canSubmitTimesheet() {
    const { timesheetObject } = this.props;
    return !timesheetObject.hasBlockingWarnings();
  }

  forceRerenderPage() {
    this.setState({});
  }

  submitTimesheet() {
    const { submitTimesheet, timesheetObject } = this.props;
    /* Blocking warnings will disable the submit button, but we reset the informational warnings when submitting */
    timesheetObject.resetWarnings();
    submitTimesheet(timesheetObject, () => this.forceRerenderPage());
  }

  renderChangedSinceApproval() {
    return (
      <div tw=" text-right leading-16" data-test-changed-since-final-state>
        <Meta tw="text-red-base">{i18n('Changed since approval.')}</Meta>
      </div>
    );
  }

  renderStickyHeader() {
    const { widgetPageHeaderRef, timesheetObject, dateFormatSetting } = this.props;
    if (!widgetPageHeaderRef?.current) return null;

    return (
      <Sticky
        $top={widgetPageHeaderRef.current.getHeaderHeight()}
        $zIndex={2}
        tw="bg-silver-lighten-30 py-16 border-silver-lighten-15 border-b"
        data-test-timesheet-sticky-header
      >
        <ContainerWrapper>
          <div tw="flex justify-between">
            <Body as="h1">{timesheetObject.printTimesheetDay(dateFormatSetting)}</Body>
            {(timesheetObject.hasEntries() ||
              timesheetObject.hasChangedSinceApproval) && (
              <div>
                {timesheetObject.hasEntries() && (
                  <TimesheetStatus
                    tw="justify-end"
                    status={timesheetObject.getStatus()}
                    showAsInlineMessage
                  />
                )}
                {timesheetObject.hasChangedSinceApproval &&
                  this.renderChangedSinceApproval()}
              </div>
            )}
          </div>
        </ContainerWrapper>
      </Sticky>
    );
  }

  // renderCounters() {
  //   return null;
  //   return (
  //     <div tw="mb-16">
  //       <TimesheetCounters
  //         label={i18n('Day Counters')}
  //         totalWorkedTimeIncludingOvertime={timesheetObject.getTotalWorkedTimeIncludingOverworkDuration()}
  //         overworkBonus={timesheetObject.getDayOverworkBonus()}
  //         totalOverworkIncludingBonus={timesheetObject.getTotalOverworkIncludingBonus()}
  //       />
  //     </div>
  //   );
  // }

  renderIssueTextArea() {
    const { issueText } = this.state;
    return (
      <FormField tw="mb-8">
        <FormField.Label $forInput="issueText">{i18n('Describe Issue')}</FormField.Label>
        <FormField.TextArea
          id="issueText"
          name="issueText"
          autoFocus
          defaultValue={issueText}
          onChange={(e) => {
            this.handleIssueTextChange(e);
          }}
        />
      </FormField>
    );
  }

  renderSubmitButton() {
    return (
      <Button
        variant="filled"
        color="primary"
        size="medium"
        onClick={(e) => {
          e.preventDefault();
          this.submitTimesheet();
        }}
        disabled={!this.canSubmitTimesheet()}
      >
        {i18n('Submit')}
      </Button>
    );
  }

  renderToggleToRequestChangesModeButton() {
    return (
      <Button
        variant="outlined"
        size="medium"
        onClick={(e) => {
          e.preventDefault();
          this.setState({ requestChangesMode: true });
        }}
      >
        {i18n('Request Changes')}
      </Button>
    );
  }

  renderSubmitRequestChangesButton() {
    return (
      <Button
        variant="filled"
        color="primary"
        size="medium"
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        {i18n('Request Changes')}
      </Button>
    );
  }

  renderCancelRequestChangesButton() {
    return (
      <Button
        size="medium"
        onClick={(e) => {
          e.preventDefault();
          this.setState({ requestChangesMode: false });
        }}
      >
        {i18n('Cancel')}
      </Button>
    );
  }

  renderActions() {
    return (
      <div tw="flex items-center justify-end space-x-4">{this.renderSubmitButton()}</div>
    );
  }

  renderContentWrapper(content, paddingTopContainer, borderTop = false) {
    return (
      <TimesheetContentWrapper $borderTop={borderTop}>
        <ContainerWrapper tw="pb-12" $paddingTop={paddingTopContainer}>
          {content}
        </ContainerWrapper>
      </TimesheetContentWrapper>
    );
  }

  renderEntries() {
    const { timesheetObject } = this.props;
    const timesheetEntryObjects = timesheetObject.getEntries();
    return (
      <div tw="space-y-12">
        {timesheetEntryObjects.map((timesheetEntryObj, index) => {
          const entryId = timesheetEntryObj.getIdWithOnlyStartOrEnd();
          return (
            <React.Fragment key={entryId}>
              <TimesheetEntryItem
                timesheetEntryObject={timesheetEntryObj}
                handleTimeEdit={this.handleTimeEdit}
                processTimeChange={this.processTimeChange}
                canEdit={timesheetObject.canEdit()}
                timesheetInFinalState={timesheetObject.isInFinalState()}
                autosaveTimesheet={this.autosaveTimesheet}
              />
              {index < timesheetEntryObjects.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderNoEntries() {
    return (
      <div tw="px-16 py-32 text-center">
        <Body>{i18n('Nothing planned.')}</Body>
      </div>
    );
  }

  renderBottomBar() {
    const { timesheetObject } = this.props;
    const { requestChangesMode } = this.state;
    if (!timesheetObject.hasEntries()) return null;
    return (
      <Sticky $bottom data-test-timesheet-actionbar>
        <TimesheetBottomActionBar>
          <ContainerWrapper>
            {requestChangesMode && this.renderIssueTextArea()}
            {this.renderActions()}
          </ContainerWrapper>
        </TimesheetBottomActionBar>
      </Sticky>
    );
  }

  render() {
    const { widgetPageHeaderRef, timesheetObject, dateFormatSetting, timeFormatSetting } =
      this.props;
    if (!widgetPageHeaderRef?.current) return null;
    const unavailabilities = timesheetObject.getUnavailabilities();
    const hasTimesheetEntries = timesheetObject.hasEntries();
    const hasUnavailabilities = timesheetObject.hasUnavailabilities();
    const paddingTopContent = widgetPageHeaderRef.current.getHeaderHeight() + 8;
    return (
      <div tw="flex flex-col grow">
        {this.renderStickyHeader()}
        <form tw="flex-grow flex flex-col">
          <div tw="flex-grow ">
            <div tw="space-y-8">
              {hasUnavailabilities &&
                this.renderContentWrapper(
                  <TimesheetUnavailabilities unavailabilities={unavailabilities} />,
                  paddingTopContent
                )}
              {hasTimesheetEntries &&
                this.renderContentWrapper(
                  this.renderEntries(),
                  hasUnavailabilities ? 8 : paddingTopContent,
                  hasUnavailabilities
                )}
              {!hasTimesheetEntries &&
                !hasUnavailabilities &&
                this.renderContentWrapper(this.renderNoEntries(), paddingTopContent)}
            </div>

            <ContainerWrapper tw="py-16">
              <TimesheetLog
                logItems={timesheetObject.getHistory()}
                dateFormatSetting={dateFormatSetting}
                timeFormatSetting={timeFormatSetting}
              />
            </ContainerWrapper>
          </div>
          {timesheetObject.canEdit() && this.renderBottomBar()}
        </form>
      </div>
    );
  }
}

TimesheetDetail.propTypes = {
  timesheetObject: PropTypes.instanceOf(YPTimesheetObject).isRequired,
  dateObject: PropTypes.shape({}).isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired,
  timeFormatSetting: PropTypes.oneOf(timeFormatOptions).isRequired,
  widgetPageHeaderRef: PropTypes.shape({
    current: PropTypes.shape({
      getHeaderHeight: PropTypes.func
    })
  }),
  autosaveTimesheet: PropTypes.func.isRequired,
  submitTimesheet: PropTypes.func.isRequired
};

TimesheetDetail.defaultProps = {
  widgetPageHeaderRef: null
};

export default TimesheetDetail;
